// Shared assessment logic. Scores are unchanged from the approved version; the
// revenue bands, grade names and recommendations were reworded in the October
// 2026 audit so they match the levels and services the site actually sells.
//
// Order is a UX decision, not a scoring one: the total is a straight sum, so
// any order scores identically. "Who leads your finances" opens because it is
// the lowest-friction question in the set; revenue - the most sensitive -
// comes second rather than first.
//
// Everything that cares which question sits where (the tier logic, the
// observation engine, the snapshot) goes through the *_Q constants below -
// a reorder here is only complete when those constants move with it.

export const LEADERSHIP_Q = 0;
export const REVENUE_Q = 1;
export const VISIBILITY_Q = 2;
export const CHALLENGE_Q = 3;
export const RECORDS_Q = 4;
export const TAX_Q = 5;
export const MILESTONE_Q = 6;

export const questions = [
  {
    question: "Who currently leads the financial management of your business?",
    options: [
      "I handle most of it myself",
      "External accountant or bookkeeper",
      "In-house accountant or finance manager",
      "Dedicated CFO or finance leadership team",
    ],
    scores: [1, 2, 3, 4],
  },
  {
    question: "What is your company’s annual revenue?",
    // The levels' own bands, so the answer maps straight to a level.
    options: ["Under £1m", "£1m–£5m", "£5m–£10m", "Over £10m"],
    scores: [1, 2, 3, 4],
  },
  {
    question:
      "How quickly can you access reliable financial information to make business decisions?",
    options: [
      "I rely mostly on bank balance and rough estimates",
      "Monthly reports from our accountant",
      "Regular management reports and dashboards",
      "Real-time financial insights and forecasting",
    ],
    scores: [1, 2, 3, 4],
  },
  {
    question:
      "Which of the following financial challenges do you experience most often?",
    options: [
      "Cash-flow visibility is sometimes unclear",
      "Financial reports arrive too late to guide decisions",
      "I’m not sure if the business is structured tax-efficiently",
      "We struggle to forecast growth confidently",
      "None of these apply",
    ],
    scores: [1, 1, 2, 2, 4],
  },
  {
    question:
      "How reliable are your company’s financial records and reports?",
    options: [
      "We are not fully confident in the accuracy of our numbers",
      "Our accountant manages things, but visibility is limited",
      "Financial records are generally reliable with occasional issues",
      "Our financial data and reporting are well structured and dependable",
    ],
    scores: [1, 2, 3, 4],
  },
  {
    question: "How would you describe your company’s tax planning approach?",
    options: [
      "Reactive - handled at year end",
      "Basic - our accountant manages it annually",
      "Proactive - we plan throughout the year",
      "Strategic - ongoing optimisation and planning",
    ],
    scores: [1, 2, 3, 4],
  },
  {
    question:
      "Are you expecting any of the following business milestones in the next 12 months?",
    options: [
      "None planned",
      "Preparing for audit or investor reporting",
      "Fundraising or investor discussions",
      "Acquisition, expansion, or exit planning",
    ],
    scores: [1, 2, 3, 4],
  },
];

export const challengeLabels: Record<number, string> = {
  0: "Cash-flow visibility",
  1: "Financial reporting",
  2: "Tax planning",
  3: "Planning and forecasting",
  4: "No immediate concerns identified",
};

export const challengeRecommendations: Record<number, string> = {
  0: "Implementing structured cash-flow monitoring and forecasting can significantly improve financial confidence and reduce operational uncertainty. Businesses that establish real-time cash-flow visibility are better positioned to manage growth, plan investment, and navigate seasonal fluctuations.",
  1: "Transitioning to timely, structured management reporting ensures financial data is available when decisions need to be made - not weeks later. Establishing a reporting cadence aligned with your decision-making cycle can materially improve responsiveness and strategic agility.",
  2: "A proactive tax planning review could uncover structuring opportunities that reduce your effective tax burden and improve net profitability. Many businesses in growth phases leave significant value on the table by not reviewing their tax position strategically.",
  3: "Building a robust financial forecasting framework would give your leadership team the confidence to plan growth initiatives with clarity. Scenario modelling and forward-looking financial planning are critical for businesses navigating expansion or fundraising.",
  4: "Your financial infrastructure appears well-managed. A strategic review could still surface optimisation opportunities as your business evolves, particularly around efficiency, reporting cadence, and forward planning.",
};

export type AssessmentTone = "optimised" | "strong" | "developing" | "attention";

export const getResult = (score: number) => {
  const max = questions.length * 4;
  const pct = (score / max) * 100;

  if (pct >= 82)
    return {
      level: "Optimised",
      tone: "optimised" as AssessmentTone,
      summary:
        "Your business demonstrates strong financial maturity with strategic finance integrated into operations and growth planning. You are well-positioned to capitalise on opportunities and navigate complex financial events.",
      recommendation:
        "Your finance function is in good order. If a fundraise, acquisition or exit is coming, CFO Advisory is the project built for it. Otherwise a discovery call is the quickest way to find out whether anything is worth changing.",
    };
  if (pct >= 61)
    return {
      level: "Established",
      tone: "strong" as AssessmentTone,
      summary:
        "Your business has solid financial processes in place and uses financial information to support decision-making. There may still be opportunities to deepen strategic finance capabilities.",
      recommendation:
        "The basics are sound. The usual next step is the Growth retainer: monthly management accounts, a KPI dashboard and cash-flow forecasting. A discovery call will tell you whether it fits.",
    };
  if (pct >= 40)
    return {
      level: "Developing",
      tone: "developing" as AssessmentTone,
      summary:
        "Some financial processes are in place, but there may still be gaps in financial visibility, planning, or strategic finance support. Addressing these gaps now could significantly strengthen your position.",
      recommendation:
        "There are gaps to close before the next year end. An Audit Readiness Review finds them and puts them in order, so you know what to fix first. A discovery call is where we agree the scope.",
    };
  return {
    level: "Needs Attention",
    tone: "attention" as AssessmentTone,
    summary:
      "Your business likely lacks structured financial oversight and may be missing opportunities for tax efficiency, financial visibility, and strategic decision support.",
    recommendation:
      "Start with the records. A Remediation Sprint clears the backlog and gets the books to a state you can rely on. A discovery call is where we agree the scope.",
  };
};

export const getObservations = (_answers: number[], selectedOptions: number[]) => {
  const observations: string[] = [];

  // Revenue
  if (selectedOptions[REVENUE_Q] === 0) {
    observations.push(
      "At the early revenue stage, establishing sound financial foundations - including reporting, tax planning and cash-flow management - can prevent costly issues as the business scales."
    );
  } else if (selectedOptions[REVENUE_Q] === 1) {
    observations.push(
      "Many businesses at £1m–£5m experience increasing financial complexity while finance leadership often remains founder-led. Establishing stronger financial structures early can significantly improve decision-making and scalability."
    );
  } else if (selectedOptions[REVENUE_Q] === 2) {
    observations.push(
      "Businesses at £5m–£10m typically benefit from stronger financial visibility, forecasting, and strategic finance support as operational complexity increases."
    );
  } else {
    observations.push(
      "At your revenue level, the business demands sophisticated financial infrastructure including real-time reporting, strategic tax planning, and embedded finance leadership to support continued growth."
    );
  }

  // Leadership
  if (selectedOptions[LEADERSHIP_Q] === 0) {
    observations.push(
      "Financial management appears to rely heavily on leadership capacity. Introducing structured financial oversight can free up leadership time and improve decision quality."
    );
  } else if (selectedOptions[LEADERSHIP_Q] === 1) {
    observations.push(
      "Many growing businesses rely on external accountants for compliance but may lack strategic financial guidance to support scaling decisions."
    );
  } else if (selectedOptions[LEADERSHIP_Q] === 2) {
    observations.push(
      "Having in-house financial resource provides a solid foundation. Ensuring this resource is supported by strategic financial planning frameworks can further strengthen decision-making."
    );
  } else {
    observations.push(
      "Dedicated CFO or finance leadership demonstrates a mature approach to financial management, enabling strategic integration of finance across the business."
    );
  }

  // Visibility
  if (selectedOptions[VISIBILITY_Q] === 0) {
    observations.push(
      "Financial visibility appears limited. Decisions may rely on incomplete or outdated financial information, which can increase risk during periods of growth."
    );
  } else if (selectedOptions[VISIBILITY_Q] === 1) {
    observations.push(
      "Monthly reporting provides a foundation for financial oversight but may still limit real-time decision-making. More frequent or automated reporting could improve responsiveness."
    );
  } else if (selectedOptions[VISIBILITY_Q] === 2) {
    observations.push(
      "Regular management reporting is supporting decision-making effectively. Enhancing this with predictive analytics and scenario modelling could add further strategic value."
    );
  }

  // Financial records reliability
  if (selectedOptions[RECORDS_Q] === 0) {
    observations.push(
      "Your responses suggest that the reliability of financial data may currently be a concern. When financial records are uncertain or incomplete, it becomes much harder to make confident business decisions, manage cash flow effectively, or prepare for external scrutiny such as audits or investor reporting. Many growing businesses encounter this situation as financial complexity increases, and strengthening financial data integrity is often one of the most valuable first steps toward improving financial visibility."
    );
  } else if (selectedOptions[RECORDS_Q] === 1) {
    observations.push(
      "Financial records appear to be managed externally, but day-to-day visibility into the underlying numbers may be limited. This is common in growing businesses where accounting is handled primarily for compliance rather than operational insight. Improving internal financial visibility through clearer reporting, dashboards, or forecasting can significantly improve decision-making confidence and operational control."
    );
  } else if (selectedOptions[RECORDS_Q] === 2) {
    observations.push(
      "Your responses suggest that financial records are broadly reliable but may occasionally lack the structure or consistency needed for deeper financial insight. As businesses grow, strengthening reporting processes and financial controls can improve confidence in the numbers and make it easier to support strategic decisions, forecasting, and future growth initiatives."
    );
  } else if (selectedOptions[RECORDS_Q] === 3) {
    observations.push(
      "Your responses indicate a strong foundation in financial record-keeping and reporting reliability. Businesses with dependable financial data are better positioned to make informed decisions, plan for growth, and respond quickly to changing financial conditions. Maintaining this level of clarity often enables organisations to move beyond basic reporting and begin using financial insight more strategically."
    );
  }

  // Tax
  if (selectedOptions[TAX_Q] === 0) {
    observations.push(
      "Tax planning appears reactive, with activity concentrated at year-end. A more proactive approach could improve tax efficiency and reduce pressure on the business during reporting periods."
    );
  } else if (selectedOptions[TAX_Q] === 1) {
    observations.push(
      "Basic tax planning is in place but may not fully capture available reliefs or structuring opportunities. Periodic strategic tax reviews can surface meaningful savings."
    );
  }

  // Milestones
  if (selectedOptions[MILESTONE_Q] >= 2) {
    observations.push(
      "With significant business milestones on the horizon, ensuring your financial infrastructure is robust and investor-ready will be critical to a successful outcome."
    );
  }

  return observations.slice(0, 4);
};

export const snapshotLevels = ["Reactive", "Emerging", "Structured", "Strategic"];

export const getSnapshotItems = (selectedOptions: number[]) => {
  const labels = [
    "Financial Leadership",
    "Financial Visibility",
    "Strategic Integration",
    "Tax Planning",
  ];
  const indices = [LEADERSHIP_Q, RECORDS_Q, VISIBILITY_Q, TAX_Q];

  return indices.map((qi, i) => ({
    label: labels[i],
    level: snapshotLevels[Math.min(selectedOptions[qi] ?? 0, 3)],
    score: (selectedOptions[qi] ?? 0) + 1,
  }));
};

/*
 * One sentence from the reader's own answers, shown before the report is
 * unlocked. The free result used to be hedged into saying very little; this
 * names the two weakest answers back to them.
 */
const weakPhrases: Array<[number, Record<number, string>]> = [
  [RECORDS_Q, { 0: "you're not fully confident in your numbers", 1: "your accountant keeps the books but you see little of them" }],
  [VISIBILITY_Q, { 0: "you run mostly on the bank balance", 1: "your reports arrive monthly from your accountant" }],
  [TAX_Q, { 0: "tax is dealt with at year end", 1: "tax planning happens once a year" }],
  [LEADERSHIP_Q, { 0: "you handle most of the finance yourself", 1: "your finances are led from outside the business" }],
];

export const getHeadline = (selectedOptions: number[]) => {
  const weak = weakPhrases
    .map(([q, phrases]) => ({ answer: selectedOptions[q], phrase: phrases[selectedOptions[q]] }))
    .filter((w) => w.phrase)
    .sort((a, b) => a.answer - b.answer)
    .slice(0, 2)
    .map((w) => w.phrase);
  const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
  if (weak.length === 2) return `${cap(weak[0])}, and ${weak[1]}: those two are where we'd start.`;
  if (weak.length === 1) return `${cap(weak[0])}: that's where we'd start.`;
  return "Your answers describe a finance function in good order. The report covers where it could go further.";
};
